import React, { memo, useEffect, useRef } from "react";
import FareCardDesktop from "../../../../components/elements/FlightCard/components/FareCard.desktop.js";
import { t } from "@bookingcom/lingojs-core";
import { Carousel, Divider, Grid } from "@bookingcom/bui-react";
import { SkeletonFlightCard } from "../../../../components/elements/Skeleton/SkeletonFlightCard.js";
import { useI18n } from "@bookingcom/lingojs-react";
import { convertFlightToFare } from "../utils.js";
import useBrandedFares from "../../../hooks/useBrandedFares.js";
import Frame from "../../../../components/elements/Frame/index.js";
import useTrackFirstFarePriceMismatch from "../../../hooks/useTrackFirstFarePriceMismatch.js";
import useEqualRowHeight from "../../../../hooks/useEqualRowHeight.js";
import { isDeltaAirlinesOffer } from "../../../../components/elements/FlightCard/utils.js";
import useIsInViewport from "../../../../hooks/useIsInViewport.js";
import flights_apex_web_delta_airlines_pilot_2026 from "../../../../utils/experiments/apex/flights_apex_web_delta_airlines_pilot_2026.js";
import flights_web_delta_airlines_pilot_phase_2 from "../../../../utils/experiments/apex/flights_web_delta_airlines_pilot_phase_2.js";
import flights_apex_tabular_branded_fares_sr from "../../../../utils/experiments/apex/flights_apex_tabular_branded_fares_sr.js";
import useClientMetrics from "../../../../hooks/useClientMetrics.js";
import useTimer from "../../../../hooks/useTimer.js";
import { trackBrandedFareFeaturesOnBackendStages } from "../../../../utils/experiments/apex/flights_apex_web_filter_branded_fare_features_on_backend.js";
import { trackAlignedBrandedFareFeaturesStages } from "../../../../utils/experiments/apex/flights_apex_web_filter_align_branded_fare_features_apps.js";
import { findFareIndex } from "../../../utils/fares.js";
const _FareSelectorDesktop = ({ flight, variant }) => {
    const i18n = useI18n();
    const { fetchStatus, brandedFareOffers } = useBrandedFares(flight.token);
    const faresAvailableFromBrandedFares = !!brandedFareOffers && brandedFareOffers?.length > 0;
    const containerRef = useRef(null);
    useEqualRowHeight(containerRef, "data-fare-card-row", [brandedFareOffers, fetchStatus]);
    useTrackFirstFarePriceMismatch(flight, brandedFareOffers);
    const brandedFareOffersToRender = faresAvailableFromBrandedFares ? brandedFareOffers : flight.brandedFareOffers;
    const hasBrandedFareOffers = (brandedFareOffersToRender?.length ?? 0) > 0;
    useEffect(() => {
        if (!hasBrandedFareOffers)
            return;
        trackBrandedFareFeaturesOnBackendStages("search_results", flight.segments);
        trackAlignedBrandedFareFeaturesStages("search_results", flight.segments);
    }, [flight.brandedFareInfo, flight.segments, hasBrandedFareOffers]);
    useEffect(() => {
        const hasDuplicateBaggageCodes = (featuresList) => {
            const baggageCodes = featuresList.filter((f) => f.category === "BAGGAGE").map((f) => f.code);
            return baggageCodes.length !== new Set(baggageCodes).size;
        };
        const baseHasDuplicates = hasDuplicateBaggageCodes(flight.brandedFareInfo?.featuresList ?? []);
        if (baseHasDuplicates) {
            flights_apex_tabular_branded_fares_sr.stages.different_baggage_allowance();
        }
    }, [flight.brandedFareInfo]);
    const isInViewport = useIsInViewport(containerRef, [flight, brandedFareOffers], { threshold: 0.8 });
    const isDelta = isDeltaAirlinesOffer(flight);
    const startTime = useRef(null);
    useEffect(() => {
        if (!isDelta)
            return;
        if (isInViewport && fetchStatus === "success" && !hasBrandedFareOffers) {
            flights_apex_web_delta_airlines_pilot_2026.goals.empty_delta_fares_seen();
        }
        if (fetchStatus === "loading" && isInViewport && !startTime.current) {
            flights_web_delta_airlines_pilot_phase_2.goals.delta_fares_loader_seen();
            startTime.current = Date.now();
        }
        if (fetchStatus === "success" && startTime.current) {
            const duration = Date.now() - startTime.current;
            flights_apex_web_delta_airlines_pilot_2026.goalsWithValue.flights_delta_fares_loader_seen_ms(duration);
            startTime.current = null;
        }
    }, [fetchStatus, isDelta, isInViewport, hasBrandedFareOffers]);
    const { trackClientMetric } = useClientMetrics();
    const loaderTimer = useTimer();
    useEffect(() => {
        if (fetchStatus === "loading" && isInViewport) {
            loaderTimer.start();
        }
        if (fetchStatus === "success") {
            const duration = loaderTimer.stop();
            if (duration)
                trackClientMetric("branded_fares_on_sr_loader_seen_ms", duration);
        }
    }, [fetchStatus, isInViewport, trackClientMetric, loaderTimer]);
    return (React.createElement("div", { ref: containerRef },
        React.createElement(Divider, null),
        React.createElement(Frame, { direction: "row", m: 4, attributes: {
                style: { maxWidth: "682px" }
            } }, hasBrandedFareOffers ? (React.createElement("div", { style: { width: "100%" } },
            React.createElement(Carousel, { size: "larger", ariaLabel: i18n.trans(t("a11y_flights_bf_carousel_label")), nextButtonAriaLabel: i18n.trans(t("a11y_flights_fare_switcher_carousel_next_button_aria_label")), previousButtonAriaLabel: i18n.trans(t("a11y_flights_fare_switcher_carousel_previous_button_aria_label")) }, brandedFareOffersToRender?.map((brandedFareOffer) => {
                const sourceOffers = brandedFareOffers ?? flight.brandedFareOffers;
                const globalIndex = findFareIndex(brandedFareOffer.token, sourceOffers);
                return (React.createElement(FareCardDesktop, { key: brandedFareOffer.token, baseOffer: flight, fareOffer: brandedFareOffer, index: globalIndex, numberOfOffers: brandedFareOffersToRender?.length, variant: variant }));
            })))) : (React.createElement("div", { style: { width: "100%" } },
            React.createElement(Grid, null,
                React.createElement(Grid.Column, { size: 4 },
                    React.createElement(FareCardDesktop, { fareOffer: convertFlightToFare(flight, i18n), baseOffer: flight, loadingStatus: fetchStatus, variant: variant })),
                fetchStatus === "loading" && (React.createElement(Grid.Column, { size: 4 },
                    React.createElement(SkeletonFlightCard, { shorterCard: true })))))))));
};
export const FareSelectorDesktop = memo(_FareSelectorDesktop);
